"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { X, Search } from "lucide-react";

interface PopularItem {
  name: string;
  image: string;
  href: string;
}

interface ServiceData {
  id: string;
  name: string;
  image: string;
  imageActive: string;
  mainImage?: string;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
  popularItems?: PopularItem[];
}

const services: ServiceData[] = [
  {
    id: "toolouer",
    name: "Multilocation TP",
    image: "/cards/toolouer.png",
    imageActive: "/cards/toolouer-active.png",
    mainImage: "/services/multilocation-tp.png",
    description:
      "Accédez à une large gamme de machines pour vos chantiers : terrassement, levage, compactage ou manutention. Toolouer vous propose du matériel fiable, disponible rapidement et adapté aux particuliers et professionnels du BTP.",
    ctaLabel: "Voir tout le matériel TP",
    ctaHref: "/categories/location?stores=Toolouer",
    popularItems: [
      { name: "Outillage pro", image: "/services/mltp-outillage.png", href: "/categories/outillage-pro" },
      { name: "Sablage, nettoyage et jardinage", image: "/services/mltp-sablage.png", href: "/categories/sablage-nettoyage-jardinage" },
      { name: "Sol, mur, béton", image: "/services/mltp-sol.png", href: "/categories/sol-mur-beton" },
      { name: "Terrassement, compactage", image: "/services/mltp-terrassement.png", href: "/categories/terrassement-compactage" },
    ],
  },
  {
    id: "locabane",
    name: "Roulottes et cabanes de chantier",
    image: "/cards/locabane.png",
    imageActive: "/cards/locabane-active.png",
    mainImage: "/services/locabane-main.png",
    description:
      "Locabane propose des bases vie et bungalows de chantier conçus pour offrir confort, praticité et sécurité aux équipes sur site. Idéal pour les chantiers longue durée comme pour les installations temporaires.",
    ctaLabel: "Voir tout le catalogue",
    ctaHref: "/boutique/locabane",
    popularItems: [
      { name: "Roulottes de chantier", image: "/services/locabane-roulotte.png", href: "/categories" },
      { name: "Toilettes de chantier", image: "/services/locabane-toilettes.png", href: "/categories" },
      { name: "Cabanes démontables", image: "/services/locabane-cabanes.png", href: "/categories" },
      { name: "Sanitaires de chantier", image: "/services/locabane-sanitaires.png", href: "/categories" },
    ],
  },
  {
    id: "locabloc",
    name: "Bases vie et bungalows de chantier",
    image: "/cards/locabloc.png",
    imageActive: "/cards/locabloc-active.png",
    mainImage: "/services/locabloc-main.png",
    description:
      "Locabloc propose des modules et containers robustes et modulables pour créer rapidement des espaces de travail, de stockage ou de vie sur vos chantiers.",
    ctaLabel: "Voir tout le catalogue",
    ctaHref: "/boutique/locabloc",
    popularItems: [
      { name: "Bungalows de chantier", image: "/services/locabloc-bungalow.png", href: "/categories/bases-vie-chantier" },
      { name: "Blocs sanitaires", image: "/services/locabloc-sanitaire.png", href: "/categories/bases-vie-chantier" },
      { name: "Guérites de chantier", image: "/services/locabloc-guerite.png", href: "/categories/bases-vie-chantier" },
      { name: "Containers de chantier", image: "/services/locabloc-container.png", href: "/categories/bases-vie-chantier" },
    ],
  },
  {
    id: "toolmatos",
    name: "Vente de consommables de chantier",
    image: "/cards/toolmatos.png",
    imageActive: "/cards/toolmatos-active.png",
    mainImage: "/services/toolmatos-main.png",
    description:
      "Toolmatos vous permet d'acheter tout le matériel et les consommables indispensables pour vos chantiers : outils, équipements de protection, accessoires et pièces.",
    ctaLabel: "Voir tout le catalogue",
    ctaHref: "/categories/achat",

    popularItems: [
      { name: "Déchets et gravats", image: "/services/toolmatos-dechets.png", href: "/boutique/toolmatos" },
      { name: "Filets, bâches et rouleaux de protection", image: "/services/toolmatos-filets.png", href: "/boutique/toolmatos" },
      { name: "Vêtements et protections EPI", image: "/services/toolmatos-epi.png", href: "/categories/vetements-protection-epi" },
      { name: "Travaux en hauteur et échafaudages fixes", image: "/services/toolmatos-hauteur.png", href: "/categories/travail-en-hauteur" },
    ],
  },
  {
    id: "immediacte",
    name: "Actes administratifs et balisage de chantiers",
    image: "/cards/immediacte.png",
    imageActive: "/cards/immediacte-active.png",
    mainImage: "/services/immediacte-main.png",
    description:
      "Immediacte simplifie toutes vos démarches administratives liées aux chantiers. Déclarations, autorisations et documents réglementaires sont gérés rapidement pour vous permettre de vous concentrer sur votre activité.",
    ctaLabel: "Voir tout le catalogue",
    ctaHref: "/boutique/immediacte",
    popularItems: [
      { name: "Actes techniques et spécifiques", image: "/services/acte-technique.png", href: "/categories/acte-technique-specifique" },
      { name: "Autorisation d'occupation du domaine public", image: "/services/autorisation-domaine.png", href: "/categories/autorisation-occupation-domaine-public" },
      { name: "Constat d'huissier", image: "/services/constat-huissier.png", href: "/categories/constat-huissier" },
      { name: "Déclaration préalable de chantier", image: "/services/declaration-prealable.png", href: "/categories/declaration-prealable-chantier" },
    ],
  },
  {
    id: "echafautop",
    name: "Échafaudages fixes et roulants",
    image: "/cards/echafautop.png",
    imageActive: "/cards/echafautop-active.png",
    mainImage: "/services/echafautop-main.png",
    description:
      "Echafautop propose des solutions d\u2019\u00e9chafaudage s\u00e9curis\u00e9es pour les travaux en hauteur. Nos \u00e9quipements r\u00e9pondent aux normes de s\u00e9curit\u00e9 et s\u2019adaptent \u00e0 tous types de chantiers.",
    ctaLabel: "Voir tous les échafaudages",
    ctaHref: "/boutique/echafautop",
    popularItems: [
      { name: "Échafaudage fixe", image: "/services/echafaudage-fixe.png", href: "/categories/echafaudage?type=location" },
      { name: "Échafaudage roulant", image: "/services/echafaudage-roulant.png", href: "/categories/echafaudage-roulant?type=location" },
      { name: "Échafaudage volant", image: "/services/echafaudage-volant.png", href: "/produits/location-echafaudage-volant-ile-de-france-1126" },
    ],
  },
];

function ServiceContent({ service }: { service: ServiceData }) {
  return (
    <div className="p-6 md:p-8 h-full flex flex-col">
      {/* Titre */}
      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
        {service.name}
      </h3>

      {/* Description avec image */}
      <div className="flex flex-col md:flex-row gap-6 mb-8">
        {service.mainImage && (
          <div className="w-full md:w-56 aspect-square relative rounded-xl overflow-hidden flex-shrink-0">
            <Image
              src={service.mainImage}
              alt={service.name}
              fill
              className="object-cover"
            />
          </div>
        )}
        <div className="flex flex-col justify-center gap-4">
          {service.description && (
            <p className="text-gray-600 leading-relaxed text-base md:text-lg">
              {service.description}
            </p>
          )}
          {service.ctaHref && (
            <Link
              href={service.ctaHref}
              className="inline-flex items-center gap-2 bg-secondary text-white px-6 py-3 rounded-lg font-medium hover:bg-secondary/90 transition-colors w-fit"
            >
              <Search className="h-4 w-4" />
              {service.ctaLabel}
            </Link>
          )}
        </div>
      </div>

      {/* Produits populaires */}
      {service.popularItems && service.popularItems.length > 0 && (
        <div>
          <h4 className="text-lg font-bold text-gray-900 mb-4">
            Produits populaires
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {service.popularItems.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className="group rounded-xl overflow-hidden flex flex-col"
              >
                <div className="relative aspect-[4/3] flex-1 bg-[#F7F7F7]">
                  <Image
                    src={item.image}
                    alt={item.name}
                    fill
                    className="object-cover"
                  />
                </div>
                {/* Bandeau nom pleine largeur */}
                <div className="bg-secondary text-white text-sm font-medium py-2 px-3 text-center">
                  {item.name}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ServicesCardsSection() {
  const router = useRouter();
  const [activeService, setActiveService] = useState<string | null>(null);
  const [arrowLeft, setArrowLeft] = useState(0);
  const cardRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const updateArrowPosition = useCallback(() => {
    if (!activeService) return;
    const cardEl = cardRefs.current[activeService];
    const triangleParent = document.getElementById("services-triangle-container");
    if (!cardEl || !triangleParent) return;
    const parentRect = triangleParent.getBoundingClientRect();
    const cardRect = cardEl.getBoundingClientRect();
    const centerX = cardRect.left + cardRect.width / 2 - parentRect.left;
    setArrowLeft(centerX);
  }, [activeService]);

  useEffect(() => {
    updateArrowPosition();
    window.addEventListener("resize", updateArrowPosition);
    return () => window.removeEventListener("resize", updateArrowPosition);
  }, [updateArrowPosition]);

  const handleCardClick = (id: string) => {
    // On mobile, navigate directly to the category page
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      const service = services.find((s) => s.id === id);
      if (service?.ctaHref) {
        router.push(service.ctaHref);
        return;
      }
    }
    setActiveService(activeService === id ? null : id);
  };

  const activeServiceData = services.find((s) => s.id === activeService);

  return (
    <section className="py-8 bg-gray-50">
      <div className="container mx-auto px-4" ref={containerRef}>
        {/* Cards */}
        <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
          {services.map((service) => {
            const isActive = activeService === service.id;
            return (
              <button
                key={service.id}
                ref={(el) => {
                  cardRefs.current[service.id] = el;
                }}
                onClick={() => handleCardClick(service.id)}
                className="cursor-pointer transition-transform duration-200 hover:-translate-y-1"
              >
                <div className="relative w-full aspect-[3/4] overflow-hidden rounded-lg">
                  <Image
                    src={isActive ? service.imageActive : service.image}
                    alt={service.name}
                    fill
                    className="object-contain"
                  />
                </div>
              </button>
            );
          })}
        </div>

        {/* Triangle + Section dépliable */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            activeService
              ? "max-h-[800px] opacity-100 mt-4"
              : "max-h-0 opacity-0 mt-0"
          }`}
        >
          {/* Triangle qui pointe vers la card active */}
          <div id="services-triangle-container" className="relative h-5">
            {activeService && (
              <div
                className="absolute bottom-0 transition-all duration-300 ease-in-out"
                style={{ left: arrowLeft - 16 }}
              >
                <div
                  className="w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-b-[20px] border-b-white"
                  style={{
                    filter: "drop-shadow(0 -2px 2px rgba(0,0,0,0.06))",
                  }}
                />
              </div>
            )}
          </div>
          <div className="relative">
            <div className="relative bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Bouton fermer */}
              <button
                onClick={() => setActiveService(null)}
                className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>

              {/* Contenu dynamique */}
              {activeServiceData && (
                <ServiceContent service={activeServiceData} />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
